export type BadgeType = "popular" | "new" | "sale" | "for-kids" | "for-women" | "premium" | "vip" | null

export interface Course {
  id: string
  title: string
  description: string
  level: string
  duration: string
  students: string
  rating: number
  price: string
  originalPrice?: string | null
  image?: string
  imageAlt?: string
  category: string
  badges?: BadgeType[]  // Changed to array for multiple badges
  sessions?: string
  hoursPerWeek?: string
  outcomes?: string[]
  syllabus?: string[]
  prerequisites?: string[]
  videoUrl?: string  // New: Telegram video link
  suitableFor?: ("kids" | "women" | "men" | "all")[]  // New: Target audience
  isPremiumCourse?: boolean  // New: For special pricing courses
}

export const allCourses: Course[] = [
  // ===== QURAN COURSES (5 courses) =====
  
  // 1. Arabic For Reading the Quran (NEW)
  {
    id: "arabic-reading-quran",
    title: "Arabic For Reading the Quran",
    description: "Master Arabic letters, vowels and word recognition specifically for Quranic reading. Build confidence in reciting the Quran correctly from absolute beginner level.",
    level: "Absolute Beginner",
    duration: "10 weeks",
    students: "510+",
    rating: 4.9,
    price: "$10-8/hr",
    originalPrice: null,
    category: "Quran",
    badges: ["popular"],
    image: "/quran-memorization.jpg",
    imageAlt: "Student learning Arabic for Quran reading",
    sessions: "3 / week",
    hoursPerWeek: "3–4h",
    outcomes: [
      "Read Quranic script fluently",
      "Understand Arabic vowel system (harakat)",
      "Build recitation confidence",
      "Recognize letters in all forms"
    ],
    prerequisites: ["No prior knowledge required"],
    syllabus: [
      "Arabic alphabet mastery",
      "Harakat & vowel marks",
      "Word joining in Quranic text",
      "Short Surah practice"
    ],
    suitableFor: ["all", "kids"],
    videoUrl: ""  // To be added
  },
  
  // 2. Quran Recitation & Tajweed
  {
    id: "quran-recitation-tajweed",
    title: "Quran Recitation & Tajweed",
    description: "Build precise Qur'an recitation: articulation (makhārij), core → advanced tajwīd rules and fluent rhythmic flow.",
    level: "Beginner → Advanced",
    duration: "6 months",
    students: "850+",
    rating: 4.9,
    price: "$10-8/hr",
    originalPrice: null,
    category: "Quran",
    badges: ["popular"],
    image: "/quran-memorization.jpg",
    imageAlt: "Student learning Quran recitation",
    sessions: "2–3 / week",
    hoursPerWeek: "2–4h",
    outcomes: [
      "Correct pronunciation & articulation",
      "Apply core & advanced Tajweed rules",
      "Fluent recitation with confidence"
    ],
    prerequisites: ["Ability to read basic Arabic letters (optional)"],
    syllabus: [
      "Arabic letters refinement & articulation",
      "Noon/Meem rules, elongations (madd)",
      "Stopping & pausing rules",
      "Advanced characteristics (sifaat) & polishing"
    ],
    suitableFor: ["all"],
    videoUrl: "/videos/2025-10-21 17.56.18.mp4"
  },
  
  // 3. Quran Memorization (Hifz)
  {
    id: "quran-memorization-hifz",
    title: "Quran Memorization (Hifz)",
    description: "Structured ḥifẓ pathway: daily pages, spaced revision loops, accountability metrics and certified coaching.",
    level: "All Levels",
    duration: "2–4 years",
    students: "420+",
    rating: 4.9,
    price: "$10-8/hr",
    originalPrice: null,
    category: "Quran",
    badges: ["sale"],
    image: "/quran-memorization.jpg",
    imageAlt: "Student memorizing Quran",
    sessions: "5 / week",
    hoursPerWeek: "5–7h",
    outcomes: [
      "Consistent memorization habit",
      "Retention & revision cycles",
      "Certified recitation standards"
    ],
    prerequisites: ["Basic fluency in reading Quran"],
    syllabus: [
      "Short Surahs & Juz Amma",
      "Progressive Juz sequencing",
      "Weekly & monthly revision loops",
      "Pre-final consolidation phase"
    ],
    suitableFor: ["all", "kids"],
    videoUrl: ""
  },
  
  // 4. Quranic Arabic (PREMIUM - appears in both Quran and Arabic categories)
  {
    id: "quranic-arabic",
    title: "Quranic Arabic (Arabic to Understand Quran)",
    description: "Decode Qur'anic Arabic: roots, morphology patterns and high‑yield lexical clusters for direct verse grasp. Understand the Quran without translation.",
    level: "Intermediate",
    duration: "6 months",
    students: "420+",
    rating: 4.7,
    price: "$12/hr",
    originalPrice: null,
    category: "Quran",
    badges: ["premium", "vip"],
    image: "/quran-memorization.jpg",
    imageAlt: "Student learning Quranic Arabic",
    sessions: "2 / week",
    hoursPerWeek: "3h",
    outcomes: [
      "Recognize root patterns",
      "Understand common Quranic phrases",
      "Translate selected verses",
      "Direct Quran comprehension"
    ],
    prerequisites: ["Basic MSA foundations or Arabic alphabet"],
    syllabus: [
      "Common Quranic roots",
      "Morphology patterns",
      "Contextual translation",
      "Verse analysis"
    ],
    suitableFor: ["all"],
    isPremiumCourse: true,
    videoUrl: ""
  },
  
  // 5. Tafseer
  {
    id: "tafseer",
    title: "Tafseer (Quran Interpretation)",
    description: "Access Qur'anic meaning via thematic translation, high‑frequency vocabulary and concise classical tafsīr insights. Deep dive into verses meanings and context.",
    level: "Intermediate",
    duration: "5 months",
    students: "260+",
    rating: 4.8,
    price: "$10-8/hr",
    originalPrice: null,
    category: "Quran",
    badges: [],
    image: "/quran-memorization.jpg",
    imageAlt: "Student studying Tafseer",
    sessions: "2 / week",
    hoursPerWeek: "3h",
    outcomes: [
      "Contextual understanding",
      "Key thematic insights",
      "Applied reflection",
      "Historical context knowledge"
    ],
    prerequisites: ["Basic Tajweed & reading ability"],
    syllabus: [
      "Selected Surah themes",
      "Linguistic insights",
      "Classical commentary survey",
      "Applied reflection sessions"
    ],
    suitableFor: ["all"],
    videoUrl: ""
  },

  // ===== ISLAMIC STUDIES (5 courses) =====
  
  // 1. Islamic Studies Foundation
  {
    id: "islamic-studies-foundation",
    title: "Islamic Studies Foundation",
    description: "Foundational Islamic studies: 'aqīdah essentials, worship rulings, seerah scaffolding and applied daily practice.",
    level: "Beginner",
    duration: "4 months",
    students: "380+",
    rating: 4.7,
    price: "$10-8/hr",
    originalPrice: null,
    category: "Islamic Studies",
    badges: [],
    image: "/quran-memorization.jpg",
    imageAlt: "Student studying Islamic foundations",
    sessions: "2 / week",
    hoursPerWeek: "2–3h",
    outcomes: [
      "Understand pillars",
      "Apply daily rulings",
      "Seerah timeline basics"
    ],
    prerequisites: ["General interest"],
    syllabus: [
      "Belief essentials",
      "Prayer essentials",
      "Seerah overview",
      "Character & conduct"
    ],
    suitableFor: ["all"],
    videoUrl: ""
  },
  
  // 2. Fiqh Essentials
  {
    id: "fiqh-essentials",
    title: "Fiqh Essentials",
    description: "Core fiqh of worship: purification flow, ṣalāh structure, fasting basics and evidence‑based application.",
    level: "Beginner",
    duration: "6 weeks",
    students: "340+",
    rating: 4.7,
    price: "$10-8/hr",
    originalPrice: null,
    category: "Islamic Studies",
    badges: [],
    image: "/quran-memorization.jpg",
    imageAlt: "Student learning Fiqh",
    sessions: "2 / week",
    hoursPerWeek: "2h",
    outcomes: [
      "Prayer correctness",
      "Purification mastery",
      "Confidence in daily worship"
    ],
    prerequisites: ["Basic Islamic awareness"],
    syllabus: [
      "Purification principles",
      "Prayer structure",
      "Fasting overview",
      "Everyday rulings"
    ],
    suitableFor: ["all"],
    videoUrl: ""
  },
  
  // 3. Seerah (Story of the Prophet)
  {
    id: "seerah",
    title: "Seerah (Story of the Prophet)",
    description: "Chronological seerah journey: pre‑revelation context, Makkan formation, Madinan transformation and legacy themes.",
    level: "Beginner → Intermediate",
    duration: "5 months",
    students: "260+",
    rating: 4.8,
    price: "$10-8/hr",
    originalPrice: null,
    category: "Islamic Studies",
    badges: [],
    image: "/quran-memorization.jpg",
    imageAlt: "Student studying Seerah",
    sessions: "2 / week",
    hoursPerWeek: "2–3h",
    outcomes: [
      "Timeline comprehension",
      "Context for revelation",
      "Role model lessons"
    ],
    prerequisites: ["General interest"],
    syllabus: [
      "Pre-revelation context",
      "Makkan phase",
      "Madinan expansion",
      "Legacy & lessons"
    ],
    suitableFor: ["all"],
    videoUrl: ""
  },
  
  // 4. Aqeedah (What We Believe In) - NEW
  {
    id: "aqeedah",
    title: "Aqeedah (What We Believe In)",
    description: "Foundational Islamic beliefs: Tawheed, pillars of Iman, prophets, and the unseen. Build correct understanding of Islamic creed and core convictions.",
    level: "Beginner → Intermediate",
    duration: "4 months",
    students: "150+",
    rating: 4.8,
    price: "$10-8/hr",
    originalPrice: null,
    category: "Islamic Studies",
    badges: ["new"],
    image: "/quran-memorization.jpg",
    imageAlt: "Student learning Aqeedah",
    sessions: "2 / week",
    hoursPerWeek: "2–3h",
    outcomes: [
      "Understand core Islamic beliefs",
      "Learn about Tawheed (Oneness of Allah)",
      "Knowledge of prophets and the unseen",
      "Strong faith foundation"
    ],
    prerequisites: ["Basic Islamic awareness"],
    syllabus: [
      "Pillars of Iman (Faith)",
      "Tawheed: Oneness of Allah",
      "Prophets and Messengers",
      "The Hereafter and unseen"
    ],
    suitableFor: ["all"],
    videoUrl: ""
  },
  
  // 5. Hadith Studies
  {
    id: "hadith-studies",
    title: "Hadith Studies",
    description: "Intro hadith methodology: classification system, authenticity principles and major collection navigation.",
    level: "Intermediate",
    duration: "3 months",
    students: "210+",
    rating: 4.8,
    price: "$10-8/hr",
    originalPrice: null,
    category: "Islamic Studies",
    badges: [],
    image: "/quran-memorization.jpg",
    imageAlt: "Student studying Hadith",
    sessions: "2 / week",
    hoursPerWeek: "2–3h",
    outcomes: [
      "Know isnad basics",
      "Recognize hadith grading terms",
      "Navigate primary collections"
    ],
    prerequisites: ["General Islamic foundation"],
    syllabus: [
      "Terminology",
      "Classification",
      "Collection overview",
      "Authenticity principles"
    ],
    suitableFor: ["all"],
    videoUrl: ""
  },
  
  // ===== ARABIC LANGUAGE (2 courses) =====
  {
    id: "modern-standard-arabic",
    title: "Modern Standard Arabic",
    description: "Progressive MSA track: core vocabulary, functional grammar, sentence patterns and graded reading fluency.",
    level: "Beginner → Intermediate",
    duration: "8 months",
    students: "650+",
    rating: 4.8,
    price: "$10-8/hr",
    originalPrice: null,
    category: "Arabic",
    badges: [],
    image: "/quran-memorization.jpg",
    imageAlt: "Student learning Modern Standard Arabic",
    sessions: "2 / week",
    hoursPerWeek: "3h",
    outcomes: [
      "Core grammar foundation",
      "Reading short texts",
      "Oral expression basics"
    ],
    prerequisites: ["Basic alphabet recognition"],
    syllabus: [
      "Core nouns & verbs",
      "Simple sentences",
      "Verb patterns",
      "Short paragraph reading"
    ],
    suitableFor: ["all"],
    videoUrl: ""
  },
  
  // 3. Egyptian Arabic Dialect (PREMIUM)
  {
    id: "egyptian-dialect",
    title: "Egyptian Arabic Dialect",
    description: "Conversational Egyptian Arabic: daily situations, media phrases and fluid beginner interaction confidence. The most widely understood Arabic dialect.",
    level: "Beginner",
    duration: "12 weeks",
    students: "300+",
    rating: 4.6,
    price: "$12/hr",
    originalPrice: null,
    category: "Arabic",
    badges: ["premium", "vip"],
    image: "/quran-memorization.jpg",
    imageAlt: "Student learning Egyptian Arabic",
    sessions: "2 / week",
    hoursPerWeek: "2–3h",
    outcomes: [
      "Basic conversational phrases",
      "Listening familiarity",
      "Introduce yourself & needs",
      "Understand Egyptian media"
    ],
    prerequisites: ["Alphabet & basic sounds"],
    syllabus: [
      "Greetings & core verbs",
      "Daily routines",
      "Questions & responses",
      "Media phrases"
    ],
    suitableFor: ["all"],
    isPremiumCourse: true,
    videoUrl: ""
  },

]

export const categoryOrder = ["All", "Quran", "Arabic", "Islamic Studies"]
